import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { isEmpty, noop } from 'lodash-es';
import { personalizedSearchEntities } from 'site-modules/personalized-search/prop-types/prop.types';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { CSSTransitionWithRef } from 'site-modules/shared/components/css-transition-with-ref/css-transition-with-ref';
import { ToggleButtonGroup } from 'site-modules/shared/components/inventory/toggle-button-group/toggle-button-group';
import { OpinionButton } from 'site-modules/personalized-search/components/opinion/opinion-button';
import { OpinionSummary } from 'site-modules/personalized-search/components/opinion/opinion-summary';
import { usePrevious } from 'site-modules/shared/hooks/use-previous';

import './opinion.scss';

export function Opinion({ opinionLabels = undefined, creativeId, vehicle = null, onRendered = noop }) {
  const [activeLabel, setActiveLabel] = useState(() => opinionLabels?.[0]?.label || '');
  const prevOpinionLabels = usePrevious(opinionLabels);

  if (prevOpinionLabels !== opinionLabels) {
    if (opinionLabels) setActiveLabel(opinionLabels[0]?.label);
  }

  useEffect(() => {
    onRendered();
  }, [onRendered]);

  const handleChange = useCallback((_, value) => {
    setActiveLabel(value);
  }, []);

  if (isEmpty(opinionLabels)) {
    return null;
  }

  return (
    <div className="opinion my-2" data-tracking-parent={creativeId}>
      <h2 className="size-20 fw-bold mb-1">What our experts say</h2>
      <ToggleButtonGroup
        className="button-group bg-transparent align-items-center flex-wrap mb-1"
        activeClassName="bg-blue-90 active-border font-weight-medium"
        inactiveClassName="btn-bg-white inactive-border font-weight-medium"
        onChange={handleChange}
        value={activeLabel}
      >
        {opinionLabels.map(({ label, labelDisplay, type }) => (
          <OpinionButton key={label} value={label}>
            <span className="opinion-button-content d-flex align-items-center size-14">
              <i
                className={classnames({
                  'icon-checkmark-circle text-blue-50': type === 'positive',
                  'icon-minus-circle text-cool-gray-50': type === 'negative',
                })}
                aria-hidden
              />
              <span className="text-cool-gray-10 ms-0_25 text-lowercase sentence-case">{labelDisplay}</span>
            </span>
          </OpinionButton>
        ))}
      </ToggleButtonGroup>
      <section className="pos-r" aria-live="polite" aria-atomic="true">
        {opinionLabels.map(opinionLabel => {
          const { label } = opinionLabel;
          const isActiveTab = activeLabel === label;
          return (
            <CSSTransitionWithRef key={label} in={isActiveTab} timeout={200} classNames="fade" mountOnEnter>
              <OpinionSummary opinionLabel={opinionLabel} id={label} isActiveTab={isActiveTab} vehicle={vehicle} />
            </CSSTransitionWithRef>
          );
        })}
      </section>
    </div>
  );
}

Opinion.propTypes = {
  creativeId: PropTypes.string.isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear,
  opinionLabels: PropTypes.arrayOf(personalizedSearchEntities.opinionLabelTypes),
  onRendered: PropTypes.func,
};
